import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Bell, Bookmark, BookmarkMinus, BookmarkPlus, ChevronDown, CircleHelp, LayoutGrid, LogOut, ShieldCheck, Target, UserRound } from "lucide-react";
import { NAV_RUBRIQUES } from "../../helpers/navCatalog.generated";
import { NAV_ICONS } from "../../helpers/navIcons";
import { useAuth } from "../../helpers/useAuth";
import { useEnregistrerFavoris, useFavoris } from "../../helpers/useFavoris";
import { useLicenseStatus } from "../../helpers/useLicenseStatus";
import { FAVORIS_MENU, GERER_FAVORIS_URL, basculer, estEpinglable, libelleGerer, messagePlein, pageCourante } from "../../nav/favoris";
import { scoreMonKeno } from "../../nav/navEspace";

/**
 * Menu « Mon espace » de l'application (D1, maquette du 16/10/2026). Mêmes classes et mêmes
 * textes que la version serveur (MagHeaderRenderer::renderEspaceMenu, comportement navEspace.ts).
 */
export const EspaceMenu = () => {
  const { authState, logout } = useAuth();
  const { data: licence } = useLicenseStatus();
  const [ouvert, setOuvert] = useState(false);
  const [score, setScore] = useState<string | null>(null);
  const charge = useRef(false);
  const { pathname } = useLocation();
  // Favoris chargés à la première ouverture, comme le score (pages serveur : initFavorisMenu).
  const [favorisDemandes, setFavorisDemandes] = useState(false);
  const { data: favoris } = useFavoris(favorisDemandes);
  const enregistrer = useEnregistrerFavoris();
  const [annonce, setAnnonce] = useState("");
  const [plein, setPlein] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const btn = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!ouvert) return;
    const dehors = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOuvert(false); };
    document.addEventListener("click", dehors);
    setFavorisDemandes(true);
    setAnnonce("");
    setPlein(false);
    if (!charge.current) {
      charge.current = true;
      fetch("/_api/auth/my-draw-tonight", { credentials: "same-origin" })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => setScore(scoreMonKeno(d)))
        .catch(() => {});
    }
    return () => document.removeEventListener("click", dehors);
  }, [ouvert]);

  if (authState.type !== "authenticated") return null;
  const { user } = authState;
  const initiale = (user.displayName || user.email || "?").trim().charAt(0).toUpperCase() || "?";
  const etat = licence?.etat ?? (licence?.hasFullAccess ? "premium" : "gratuit");
  const jours = licence?.trialDaysRemaining ?? null;
  const fermer = () => setOuvert(false);
  const page = pageCourante(pathname);
  const epinglee = favoris?.favoris.some((f) => f.path === page) ?? false;
  const epinglable = !!favoris && estEpinglable(favoris, page);
  const basculerPage = () => {
    if (!favoris || enregistrer.isPending) return;
    const paths = basculer(favoris.favoris.map((f) => f.path), page, favoris.max, true);
    if (!paths) {
      setPlein(true);
      setAnnonce(messagePlein(favoris.max));
      return;
    }
    enregistrer.mutate(paths, {
      onSuccess: () => setAnnonce(epinglee ? "Page retirée des favoris." : "Page ajoutée aux favoris."),
      onError: () => setAnnonce("Favoris non enregistrés : vérifiez votre connexion et réessayez."),
    });
  };

  return (
    <div className="km-me" ref={box} onKeyDown={(e) => { if (e.key === "Escape" && ouvert) { setOuvert(false); btn.current?.focus(); } }}>
      <button type="button" className="km-me__btn" ref={btn} aria-expanded={ouvert} aria-controls="km-me-menu" aria-label="Mon espace" onClick={() => setOuvert((o) => !o)}>
        <span className="km-me__av" aria-hidden="true">{initiale}</span><span className="km-me__nm">Mon espace</span><ChevronDown aria-hidden="true" />
      </button>
      <div className="km-me__menu" id="km-me-menu" hidden={!ouvert} data-nav-emplacement="menu_espace" onClick={(e) => { if ((e.target as Element).closest("a")) fermer(); }}>
        <div className="km-me__rubs">
          <p className="km-me__h">Rubriques</p>
          {NAV_RUBRIQUES.map((r) => {
            const Icon = NAV_ICONS[r.icon];
            return <a key={r.key} href={r.hubPath} data-nav-rubrique={r.key}>{Icon && <Icon aria-hidden="true" />}{r.label}</a>;
          })}
          <hr />
        </div>
        <p className="km-me__h">Mon espace</p>
        <Link to="/home/"><LayoutGrid aria-hidden="true" />Tableau de bord</Link>
        <Link to="/mon-keno/"><Target aria-hidden="true" />Mes grilles{score && <small>{score}</small>}</Link>
        <Link to="/mon-keno/?tab=alertes"><Bell aria-hidden="true" />Mes alertes</Link>
        <Link to="/compte/"><UserRound aria-hidden="true" />Mon compte</Link>
        {etat === "gratuit" && (
          <span className="km-me__state"><strong>Compte gratuit</strong>{licence?.hadTrial ? "Essai Premium utilisé. " : "Essai Premium de 14 jours disponible. "}<Link className="km-me__inline" to="/tarifs/?cta_source=menu_espace">Passer Premium</Link></span>
        )}
        {etat === "essai" && <span className="km-me__state"><strong>Essai Premium : encore {jours ?? 14} jours</strong>Tout Premium jusqu'à la fin de l'essai.</span>}
        {etat === "premium" && <span className="km-me__state"><strong>Premium actif</strong>Formule et échéance dans Mon compte.</span>}
        {etat === "impaye" && <span className="km-me__state km-me__state--warn"><strong>Paiement refusé</strong>Mettez à jour votre carte pour garder Premium.</span>}
        {favoris && (favoris.favoris.length > 0 || epinglable) && (
          <div className="km-me__fav">
            <hr />
            <p className="km-me__h">Mes favoris</p>
            {/* En tête du groupe : un ajout insère un lien dessous, le bouton ne bouge pas sous le curseur.
                Jamais désactivé : le focus clavier reste dessus (double clic bloqué dans basculerPage). */}
            {epinglable && (
              <button type="button" className="km-me__item km-me__fav-btn" aria-busy={enregistrer.isPending || undefined} onClick={basculerPage}>
                {epinglee ? <BookmarkMinus aria-hidden="true" /> : <BookmarkPlus aria-hidden="true" />}
                {plein && !epinglee ? messagePlein(favoris.max) : epinglee ? "Retirer cette page des favoris" : "Ajouter cette page aux favoris"}
              </button>
            )}
            <span className="km-me__sr" aria-live="polite">{annonce}</span>
            <div data-km-favoris-liste>
              {favoris.favoris.slice(0, FAVORIS_MENU).map((f) => {
                const courant = f.path === page ? "page" as const : undefined;
                return f.isStatic
                  ? <a key={f.path} href={f.href} aria-current={courant}><Bookmark aria-hidden="true" />{f.label}</a>
                  : <Link key={f.path} to={f.href} aria-current={courant}><Bookmark aria-hidden="true" />{f.label}</Link>;
              })}
            </div>
            {favoris.favoris.length > 0 && <Link className="km-me__fav-gerer" to={GERER_FAVORIS_URL}>{libelleGerer(favoris.favoris.length)}</Link>}
          </div>
        )}
        <hr />
        {user.role === "admin" && <Link to="/espace-admin/"><ShieldCheck aria-hidden="true" />Espace admin</Link>}
        <Link to="/contact/"><CircleHelp aria-hidden="true" />Aide et contact</Link>
        <button type="button" className="km-me__item" onClick={async () => { await logout(); window.location.href = "/"; }}><LogOut aria-hidden="true" />Se déconnecter</button>
      </div>
    </div>
  );
};
